<template>
  <LayoutBasePage :bg-color="jobsStore?.apiData?.Payload?.BackgroundColor">
    <template #header>
      <LayoutBaseHeader
        :logo-path="jobsStore?.apiData?.Payload?.CompanyLogoPath ?? ''"
        :title="companyName"
      >
        <template #sub-title>
          <div
            v-if="jobsStore?.apiData?.Payload?.TotalCount"
            class="mt-4 text-md text-zinc-500"
          >
            {{
              `${LANG.totalJobs[jobsStore.lang]} ${
                jobsStore?.apiData?.Payload.TotalCount ?? '...'
              }`
            }}
          </div>
        </template>
      </LayoutBaseHeader>
    </template>
    <main
      :style="{ minHeight: 'calc(100dvh - 290px)' }"
      class="font-sans h-full grow flex flex-col text-textMain"
      :dir="jobsStore.dir"
    >
      <div class="flex flex-col items-center grow">
        <div
          class="lg:relative flex flex-col justify-start grow items-stretch gap-[24px] lg:mt-[22px]"
        >
          <div class="relative">
            <SocialShare
              :title="`${companyName} - Jobs`"
              :description="companyName"
            />
            <JobsBaseFilter
              class="flex justify-center gap-4 grow pt-8"
              @search="search"
            />
            <JobsBaseGrid
              v-show="jobsStore?.jobs"
              :jobs="jobsStore?.jobs ?? []"
              :lang="jobsStore.lang"
              :loading="jobsStore.loading"
              @load-more-results="loadMoreResluts"
            />
            <JobsGridLoadingSkeleton v-show="jobsStore.loading" />
          </div>
        </div>
      </div>
      <ClientOnly>
        <BasePopup />
      </ClientOnly>
    </main>
  </LayoutBasePage>
</template>
<script setup lang="ts">
import { useRouter, useRoute } from 'vue-router';
import { LANG } from '~/config/lang';
import { useJobs } from '~/store/useJobs';
import { IJobsByShortLink } from '~/types/jobs';
import BasePopup from '~/components/BasePopup.vue';
import { LangType } from '~/types/lang';

const config = useRuntimeConfig();
const jobsStore = useJobs();
const route = useRoute();
const router = useRouter();

const companyName = computed(() => {
  return jobsStore.apiData?.Payload?.CompanyName ?? '';
});

// Set the language
onMounted(() => {
  jobsStore.lang = process.client
    ? (navigator?.language?.split('-')?.[0] as LangType) ?? 'he'
    : 'he';
});

async function loadMoreResluts() {
  const data = await fetchJobs();
  data && jobsStore.fetchData(data);
}

async function search(value: { tags: string[]; regions: number[] }) {
  // if (
  //   jobsStore?.regionIds &&
  //   isSameArray(jobsStore.regionIds, value.regions) &&
  //   jobsStore?.tagWords &&
  //   isSameArray(jobsStore.tagWords, value.tags)
  // ) {
  //   return;
  // }
  jobsStore.fromRow;
  jobsStore.clearApiData();

  jobsStore.regionIds = value.regions;
  jobsStore.tagWords = value.tags;

  const data = await fetchJobs();
  data && jobsStore.fetchData(data);
}

const shortLinkId = route.params?.shortlinkId as string;
jobsStore.shortlinkId = shortLinkId;
jobsStore.countryCode = 'IS';
jobsStore.languageId = jobsStore.langCode;

async function fetchJobs(getLists: boolean = false) {
  if (jobsStore.endOfResults || jobsStore.loading) {
    console.log('Loading...');
    return;
  }

  jobsStore.shortlinkBaseUrl = `${config.public.baseUrl}/vacancy/{0}`;
  jobsStore.loading = true;

  const res = await useFetch<IJobsByShortLink>(
    config.public.apiJobsRoute as string,
    {
      method: 'POST',
      body: jobsStore.query,
      baseURL: process.client ? window.location.origin : '/',
      credentials: 'omit',
      server: getLists,
      headers: { 'content-type': 'application/json' },
    }
  );

  jobsStore.loading = false;
  console.log('jobs data', res.data?.value);
  return res.data?.value;
}

onServerPrefetch(async () => {
  jobsStore.clearApiData();
  // const data = await fetchJobs(true);
  // data && jobsStore.fetchData(data);
  // jobsStore.loading = false;
});

useHead({
  title: computed(
    () => `${companyName.value} ${companyName.value ? '-' : ''} Jobs`
  ),
  htmlAttrs: {
    prefix: 'og: http://ogp.me/ns#',
    xmlns: 'http://www.w3.org/1999/xhtml',
    'xmlns:og': 'http://ogp.me/ns#',
    'xmlns:fb': 'http://www.facebook.com/2008/fbml',
  },
  meta: [
    {
      property: 'og:url',
      content: `${config.public.baseUrl}/jobs/${router?.currentRoute?.value?.params?.shortlinkId}`,
    },
    {
      property: 'og:image',
      name: 'twitter:image',
      // @ts-ignore
      itemprop: 'image primaryImageOfPage',
      content: jobsStore.apiData?.Payload?.CompanyLogoPath ?? '',
    },
    {
      property: 'og:title',
      name: 'twitter:title',
      // @ts-ignore
      itemprop: 'name',
      content: `${companyName.value} - Jobs`,
    },
    {
      property: 'og:description',
      name: 'twitter:description',
      // @ts-ignore
      itemprop: 'description',
      content: `${companyName.value}`,
    },
    {
      property: 'og:site_name',
      content: 'MiniSite',
    },
    {
      property: 'og:type',
      content: 'website',
    },
    {
      name: 'twitter:card',
      content: companyName.value,
    },
    {
      property: 'og:image:type',
      content: 'image/jpeg',
    },
    {
      property: 'og:image:width',
      content: '150',
    },
    {
      property: 'og:image:height',
      content: '150',
    },
    {
      property: 'twitter:image',
      content: jobsStore.apiData?.Payload?.CompanyLogoPath ?? '',
    },
    {
      property: 'twitter:title',
      content: `${companyName.value} - Jobs`,
    },
    {
      property: 'twitter:description',
      content: companyName.value,
    },
  ],
});
</script>

<style>
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>
